import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    suivreOuverture();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    /* Pas d'ouverture anticipée (avant la fin de l'ajout) : le tiroir montrait alors ~1 s
       l'ANCIEN contenu (panier vidé par le checkout direct, ou article en moins). Il s'ouvre
       plus bas, une fois l'ajout terminé et son contenu remplacé par le panier à jour. */

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        // Ouverture à l'image suivante : le contenu du tiroir (remplacé par cart-items-component
        // sur la même réponse) est alors déjà à jour quand il apparaît.
        const openAndSettle = () => {
          requestAnimationFrame(() => {
            if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
            settle();
          });
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}


/*
 * Classe « panier-ouvert » sur <html> tant que le tiroir est ouvert : l'ATC collant (et tout autre
 * élément flottant qui s'y réfère en CSS) se retire. Un observateur d'attribut couvre toutes les
 * ouvertures, y compris la restauration après navigation qui n'émet aucun événement.
 */
function suivreOuverture() {
  const tiroir = document.getElementById('cart-drawer');
  if (!tiroir || /** @type {any} */ (tiroir).__suiviOuverture) return;
  /** @type {any} */ (tiroir).__suiviOuverture = true;
  const maj = () => document.documentElement.classList.toggle('panier-ouvert', tiroir.hasAttribute('open'));
  new MutationObserver(maj).observe(tiroir, { attributes: true, attributeFilter: ['open'] });
  maj();
}

/*
 * Départ vers le paiement depuis le tiroir (bouton « Procéder au paiement » ou paiement
 * accéléré) : au moment où la page est quittée (« pagehide »), le tiroir est fermé sur-le-champ,
 * sans animation. La page entre ainsi dans le cache du navigateur tiroir FERMÉ : au retour
 * (bouton précédent), elle revient propre, sans fenêtre modale figée ni verrou de défilement,
 * et le tiroir n'est pas non plus rouvert par la mémoire de session (ordinateur).
 * Rien n'est visible : la page est déjà quittée. Le paiement lui-même n'est pas touché (le
 * formulaire est déjà envoyé, la navigation déjà engagée).
 */
function suivreDepartPaiement() {
  const w = /** @type {any} */ (window);
  if (w.__departPaiementSuivi) return;
  w.__departPaiementSuivi = true;

  let depart = 0;
  const DECLENCHEURS = '[name="checkout"], shopify-accelerated-checkout-cart, .additional-checkout-buttons';

  /** @param {Event} e */
  const noter = (e) => {
    const cible = e.target instanceof Element ? e.target : null;
    if (cible && cible.closest('#cart-drawer') && cible.closest(DECLENCHEURS)) depart = Date.now();
  };
  document.addEventListener('click', noter, true);
  document.addEventListener(
    'submit',
    (e) => {
      const envoi = /** @type {any} */ (e).submitter;
      if (envoi instanceof Element && envoi.getAttribute('name') === 'checkout' && envoi.closest('#cart-drawer')) {
        depart = Date.now();
      }
    },
    true
  );

  window.addEventListener('pagehide', () => {
    if (!depart || Date.now() - depart > 120000) return;
    depart = 0;
    const tiroir = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (tiroir && typeof tiroir.fermerSansAnimation === 'function') tiroir.fermerSansAnimation();
  });
}
suivreDepartPaiement();

/*
 * À l'ouverture, le focus va sur la croix (accessibilité). Plusieurs navigateurs mobiles
 * affichent alors l'anneau de focus même après un simple toucher : c'était le « trait sous la
 * croix ». L'anneau ne s'affiche donc que si le client navigue au clavier (voir le CSS
 * .panier-fermer dans snippets/cart-drawer.liquid).
 */
function suivreClavier() {
  const w = /** @type {any} */ (window);
  if (w.__modaliteSuivie) return;
  w.__modaliteSuivie = true;
  const racine = document.documentElement;
  const TOUCHES = /^(Tab|Enter| |Escape|Arrow)/;
  document.addEventListener(
    'keydown',
    (e) => {
      if (TOUCHES.test(String(e.key || ''))) racine.setAttribute('data-modalite-clavier', '');
    },
    true
  );
  const pointeur = () => racine.removeAttribute('data-modalite-clavier');
  document.addEventListener('mousedown', pointeur, true);
  document.addEventListener('touchstart', pointeur, { capture: true, passive: true });
}
suivreClavier();

/*
 * Éditeur de thème : sélectionner la section « Panier » ou un bloc d'une de ses zones ouvre le
 * tiroir, pour que le marchand voie ce qu'il règle.
 */
if (window.Shopify && /** @type {any} */ (window.Shopify).designMode) {
  /** @param {Event} e */
  const ouvrirSiPanier = (e) => {
    const cible = e.target instanceof Element ? e.target : null;
    const tiroir = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (!cible || !tiroir) return;
    const dansPanier = tiroir.contains(cible) || cible.contains(tiroir);
    if (dansPanier && typeof tiroir.open === 'function' && !tiroir.hasAttribute('open')) tiroir.open();
  };
  document.addEventListener('shopify:section:select', ouvrirSiPanier);
  document.addEventListener('shopify:block:select', ouvrirSiPanier);
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
suivreOuverture();

